import React, { useState, useEffect } from "react";
import SEO from "../seo";
const SetUpYourPayments = () => {
  const [activeSection, setActiveSection] = useState("payment-methods");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "payment-methods",
        "connect-provider",
        "region-customization",
        "secure-compliant",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Set Up Your Payments – xPage Help Center"
        description="Learn how to set up payment methods for your xPage store. Connect Stripe, PayPal, Apple Pay, COD, and more for a smooth checkout experience."
        keywords="xPage payment setup, connect Stripe, PayPal, Apple Pay, COD, PCI-compliant, online store payments"
        canonical="https://help.xpage.ai/help/quick-launch-checklist/Set%20Up%20Your%20Payments"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title */}
        <h1 className="text-systemTitle text-2xl font-semibold mb-3">
          Set Up Your Payments
        </h1>

        {/* Introduction Paragraph */}
        <p className="text-sm text-systemLabel mb-6">
          Accepting payments is a critical part of launching your landing pages.
          With xPage, you can connect multiple payment methods in minutes,
          ensuring a smooth checkout experience for every customer, no matter
          where they shop from.
        </p>

        {/* Choose Payment Methods Section */}
        <section id="payment-methods" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Choose your payment providers
          </h2>

          <p className="text-sm text-systemLabel mt-2 mb-4">
            xPage supports a wide range of global and regional payment
            providers, including:
          </p>

          <div className="grid md:grid-cols-1 gap-2">
            <ul className="list-disc list-inside space-y-2">
              <li className="text-sm text-systemLabel">
                <span className="font-semibold text-systemTitle">Stripe:</span>{" "}
                Accept all major credit and debit cards worldwide with
                industry-leading security, instant payouts, fraud protection,
                and a frictionless checkout experience. Perfect for scaling fast
                with global reach and reliable performance.
              </li>
              <li className="text-sm text-systemLabel">
                <span className="font-semibold text-systemTitle">
                  PayPal ( coming soon ):{" "}
                </span>{" "}
                Trusted digital wallet for quick and secure online payments.
              </li>
              <li className="text-sm text-systemLabel">
                <span className="font-semibold text-systemTitle">
                  Airwallex ( coming soon ):{" "}
                </span>{" "}
                Multi-currency global payments built for international sellers.
              </li>
              <li className="text-sm text-systemLabel">
                <span className="font-semibold text-systemTitle">
                  PagBrasil Cartão de Crédito (coming soon):
                </span>{" "}
                Local Brazilian credit card processing optimized for LATAM
                conversions. Activate the options that match your customers and
                region.
              </li>

              <li className="text-sm text-systemLabel">
                <span className="font-semibold text-systemTitle">
                  Cash on Delivery (COD):
                </span>{" "}
                Ideal for certain markets
              </li>
            </ul>
          </div>

          <p className="text-sm text-systemLabel mt-4">
            You can enable one or multiple options based on your customer
            preferences and region.
          </p>
        </section>
        {/* How to Connect Section */}
        <section id="connect-provider" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            How to connect a payment provider
          </h2>

          <p className="text-sm text-systemLabel mt-2 mb-4">To get started:</p>

          <ol className="list-decimal list-inside space-y-1 mb-4">
            <li className="text-sm text-systemLabel mb-2">
              <span className="font-semibold">Go to your Settings</span> in the
              xPage dashboard.
            </li>
            <li className="text-sm text-systemLabel mb-2">
              <span className="font-semibold">Click Payment Settings.</span>
            </li>
            <li className="text-sm text-systemLabel mb-2">
              <span className="font-semibold">Choose the provider</span> you
              want to connect (e.g., Stripe).
            </li>
            <li className="text-sm text-systemLabel mb-2">
              <span className="font-semibold">
                Follow the integration prompts
              </span>{" "}
              to log in and authorize your account.
            </li>
          </ol>

          <p className="text-sm text-systemLabel">
            For providers like COD, xPage allows you to customize delivery
            instructions, regional availability, and payment terms.
          </p>
        </section>

        {/* Region-based Customization Section */}
        <section id="region-customization" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Region-based checkout customization
          </h2>

          <p className="text-sm text-systemLabel mt-2">
            xPage lets you tailor checkout fields based on payment method and
            customer location. For example, you can request ID verification for
            COD in specific countries or adjust currency display by region.
          </p>
        </section>

        {/* Secure & Compliant Section */}
        <section id="secure-compliant" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Secure & compliant
          </h2>

          <p className="text-sm text-systemLabel mt-2">
            All payment integrations on xPage are PCI-compliant and secured with
            end-to-end encryption. Whether customers pay by card or choose local
            options, their transactions remain safe and seamless.
          </p>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#payment-methods"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "payment-methods"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Payment Methods
              </a>
            </li>
            <li>
              <a
                href="#connect-provider"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "connect-provider"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Connect Provider
              </a>
            </li>
            <li>
              <a
                href="#region-customization"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "region-customization"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Region Customization
              </a>
            </li>
            <li>
              <a
                href="#secure-compliant"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "secure-compliant"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Secure & Compliant
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SetUpYourPayments;
